/**
 * The filters column of the collection page, sticky in both directions with a
 * single scroll: the page's (8 Oct).
 *
 * The column is taller than the window with its groups open (1622px against
 * 900 on /collections/coyote-malo at 1440). assets/mm-tokens.css, section 47,
 * makes it sticky with the height of the window and a scroll of its own, and
 * the user found two scrolls confusing: the page moves while the filters stay
 * cut off until someone scrolls inside them.
 *
 * Here the column travels with the page until its last filter is in view and
 * holds there; on the way up it travels until its first filter is in view and
 * holds there. CSS can only do one of the two (a negative `top`), so `top` is
 * moved by the same distance the page scrolls and clamped between both ends.
 *
 * This file only switches the behaviour on, by writing `data-mm-sticky="page"`
 * and `--mm-filters-top` on the column; the rules that read them are next to
 * the fallback in section 47. Take the <script> out of blocks/filters.liquid
 * and the column goes back to scrolling on its own.
 */

const GAP = 24;
const desktop = matchMedia('(min-width: 750px)');

/** @param {HTMLElement} column */
function follow(column) {
  const scroller = column.closest('.page-wrapper');
  if (!(scroller instanceof HTMLElement)) return;

  let top = 0;
  let last = scroller.scrollTop;
  let frame = 0;

  const limits = () => {
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 80;
    const max = header + GAP;
    // When the column fits, both ends are the same and it is a plain sticky.
    const min = Math.min(max, scroller.clientHeight - column.offsetHeight - GAP);

    return { min, max };
  };

  const update = () => {
    frame = 0;

    if (!desktop.matches) {
      column.removeAttribute('data-mm-sticky');
      column.style.removeProperty('--mm-filters-top');
      return;
    }

    const { min, max } = limits();
    const current = scroller.scrollTop;

    if (!column.hasAttribute('data-mm-sticky')) {
      column.setAttribute('data-mm-sticky', 'page');
      top = max;
    } else {
      top = Math.min(max, Math.max(min, top - (current - last)));
    }

    last = current;
    column.style.setProperty('--mm-filters-top', `${Math.round(top)}px`);
  };

  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(update);
  };

  scroller.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule, { passive: true });
  desktop.addEventListener('change', schedule);
  // Opening or closing a group changes the height and with it the lower end.
  new ResizeObserver(schedule).observe(column);

  update();
}

/** @type {WeakSet<Element>} */
const seen = new WeakSet();

function init() {
  for (const column of document.querySelectorAll('.facets-block-wrapper--vertical .facets.facets--vertical')) {
    if (column instanceof HTMLElement && !seen.has(column)) {
      seen.add(column);
      follow(column);
    }
  }
}

init();
// Filtering re-renders the section and can replace the column.
new MutationObserver(init).observe(document.querySelector('#MainContent') ?? document.body, {
  childList: true,
  subtree: true,
});
